// Pestaña Venta del detalle del Admin VL y piezas comunes de sus pestañas (InfoRow, TabSection, TabNote).
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { ApiError } from '@/api/errors';
import { formatMXN } from '@/shared/lib/format/money';
import { saleAmounts } from '@/shared/lib/format/saleReceipt';
import { hasEquipment } from '@/shared/patterns/SaleTicket';
import type { SaleSlots } from '@/shared/patterns/SaleReceipt';
import { CommissionAccrualStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionAccrualStatusEnum';
import {
    DETAIL_SECTION_TITLE,
    DetailRow,
    PendingDetailRow,
    SaleFactsSection,
} from '@/domains/sales/portal/mysales/components/SaleDetailSections';
import {
    EXTERNAL_FOLIO_NOTE,
    financierKeyOf,
    financierName,
    PENDING_CELL,
    paymentMethodLabel,
    saleAlert,
    saleTypeLabel,
} from '@/domains/sales/portal/mysales/lib/mySalesFormat';
import { financierChipOf } from '@/domains/sales/portal/store/lib/storeFormat';
import { soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import { Badge, CopyableId } from '@/kit';
import { useAccrualBySale } from '@/domains/retail/admin/comisiones/api/commissionQueries';
import { ventaDetailFacts } from '../../lib/ventaDetailFacts';
import { accrualAmountCents } from '../../lib/ventasMetrics';
import { CANCEL_SALE_BUTTON } from '@/shared/patterns/CancelSale';
import { CancelSaleAction } from '../CancelSaleAction';
import type { SaleDetailStatus } from '../../lib/cancelAvailability';
import { CancellationSection } from './CancellationSection';
import { CentralSection } from './CentralSection';
import { PaymentBreakdown } from '@/shared/patterns/SalePayments/PaymentBreakdown';
import { NO_CHARGE_LABEL, isNoCharge, toPaymentLines } from '@/shared/lib/format/salePaymentLines';

/** Falta de dato = guion visible. Nunca una celda vacía: el hueco tiene que verse como hueco. */
export function dash(value: string | null | undefined): string {
    return value ?? PENDING_CELL;
}

/** Fecha larga del detalle, con el mes en letras y en la zona del piso de venta. */
export function formatFechaHora(iso: string): string {
    return formatDateTimeMx(iso);
}

export interface InfoRowProps {
    label: string;
    value: ReactNode;
    /** Valores que se leen carácter por carácter (folio, CURP, IMEI, SKU) van en monoespaciada. */
    mono?: boolean;
}

/** Fila clave/valor de las pestañas: la `.info-row` de 78px del mock. */
export function InfoRow({ label, value, mono }: InfoRowProps) {
    return (
        <DetailRow
            label={label}
            value={mono ? <span className="font-mono">{value}</span> : value}
        />
    );
}

/** Bloque de filas de una pestaña: título en mayúsculas y filas planas. */
export function TabSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section>
            <h3 className={DETAIL_SECTION_TITLE}>{title}</h3>
            {children}
        </section>
    );
}

export type TabNoteTone = 'info' | 'warn' | 'success' | 'danger';

const NOTE_TONE: Record<TabNoteTone, string> = {
    info: 'bg-info-soft text-info',
    warn: 'bg-warning-soft text-warning',
    success: 'bg-success-soft text-success',
    danger: 'bg-danger-soft text-danger',
};

/** Recuadro de aviso al pie de una pestaña, con el tono que le toque. */
export function TabNote({
    children,
    variant = 'info',
}: {
    children: ReactNode;
    variant?: TabNoteTone;
}) {
    return (
        <p
            className={cn(
                'mt-3 rounded-md px-2.5 py-2 text-[11px] leading-relaxed',
                NOTE_TONE[variant],
            )}
        >
            {children}
        </p>
    );
}

/** Cuerpo de cada pestaña: la primera sección no lleva aire arriba. */
export const TAB_BODY = '[&>section:first-child>h3]:mt-0';

const ALERT_TONE_TO_NOTE: Record<string, TabNoteTone> = {
    info: 'info',
    success: 'success',
    warning: 'warn',
    danger: 'danger',
};

function money(cents: number): string {
    return formatMXN(cents / 100);
}

/** Comisión del vendedor por esta venta, del devengo del motor; sin devengo, guion con su porqué. */
function CommissionRow({ sale }: { sale: BackofficeSale }) {
    const sellerId = sale.sellerRetailUserId ?? null;
    const query = useAccrualBySale(sale.saleId, sellerId, sale.soldAt);
    const label = 'Comisión vendedor';

    if (sellerId === null) {
        return (
            <PendingDetailRow
                label={label}
                reason="La venta no identifica a su vendedor, así que no hay comisión que consultar."
            />
        );
    }
    if (query.isLoading) return <DetailRow label={label} value="…" />;

    const accrual = query.data?.accrual;
    if (!accrual) {
        const status = query.error instanceof ApiError ? query.error.httpStatus : null;
        const reason =
            status === 404
                ? 'Aún no hay comisión registrada para esta venta. Si es reciente, puede estar en proceso.'
                : status === 403
                  ? 'No tienes permiso para ver las comisiones.'
                  : 'No se pudo leer la comisión de esta venta.';
        return <PendingDetailRow label={label} reason={reason} />;
    }

    const amount = money(accrualAmountCents(accrual));
    if (accrual.status === CommissionAccrualStatusEnum.REVERSED) {
        return (
            <DetailRow
                label={label}
                value={
                    <span className="text-danger">
                        <s className="text-muted-foreground">{amount}</s> · Se descuenta del corte
                    </span>
                }
            />
        );
    }
    return <DetailRow label={label} value={<span className="text-success">+ {amount}</span>} />;
}

/** Tipo, financiera y su folio (fuera del contado), método de cobro y confirmación de caja. */
function PaymentSection({ sale }: { sale: BackofficeSale }) {
    const plainCash = sale.type === 'CASH' && !sale.externalProvider;
    const chip = financierChipOf(sale.type, sale.externalProvider);
    const confirmed = sale.paymentDetail?.cashConfirmedInRegister;
    const payments = toPaymentLines(sale.payments);
    return (
        <TabSection title="Forma de pago">
            <DetailRow label="Tipo" value={saleTypeLabel(sale.type)} />
            {plainCash ? null : (
                <>
                    <DetailRow
                        label="Financiera"
                        value={
                            <Badge variant={chip.variant} className="px-2 py-0 text-[10px]">
                                {financierName(financierKeyOf(sale.type, sale.externalProvider))}
                            </Badge>
                        }
                    />
                    {sale.creditId ? (
                        <DetailRow
                            label="Folio financiera"
                            value={
                                <CopyableId
                                    label="Folio financiera"
                                    value={sale.creditId}
                                    className="[&>span]:sr-only"
                                />
                            }
                        />
                    ) : (
                        <PendingDetailRow label="Folio financiera" reason={EXTERNAL_FOLIO_NOTE} />
                    )}
                </>
            )}
            {payments ? (
                <PaymentBreakdown lines={payments} className="mt-2" />
            ) : (
                <>
                    <DetailRow
                        label="Método de cobro"
                        value={
                            isNoCharge(sale.paymentMethod, sale.amountCents)
                                ? NO_CHARGE_LABEL
                                : paymentMethodLabel(sale.paymentMethod)
                        }
                    />
                    {confirmed === undefined ? null : (
                        <DetailRow
                            label="Confirmado"
                            value={
                                confirmed
                                    ? 'El efectivo se confirmó en caja'
                                    : 'Sin confirmar en caja'
                            }
                        />
                    )}
                </>
            )}
        </TabSection>
    );
}

/** Precio del equipo, accesorios si los hubo, el total y la comisión del vendedor. */
function AmountsSection({ sale }: { sale: BackofficeSale }) {
    const withEquipment = hasEquipment(sale);
    const amounts = saleAmounts(sale.lines, withEquipment);
    const equipmentCents = amounts?.equipmentCents ?? sale.equipmentPriceCents;
    return (
        <TabSection title="Importes">
            {withEquipment && (
                <DetailRow
                    label="Precio equipo"
                    value={
                        equipmentCents === null ? (
                            <span className="text-muted-foreground">{PENDING_CELL}</span>
                        ) : (
                            <span className="font-semibold">{money(equipmentCents)}</span>
                        )
                    }
                />
            )}
            {amounts && amounts.accessoriesCents > 0 ? (
                <DetailRow label="Accesorios" value={money(amounts.accessoriesCents)} />
            ) : null}
            <DetailRow
                label="Total de la venta"
                value={money(amounts?.totalCents ?? soldCentsOf(sale))}
            />
            <CommissionRow sale={sale} />
        </TabSection>
    );
}

/** Pestaña Venta: detalles, forma de pago, importes, el aviso del caso y la cancelación. */
export function VentaTab({
    sale,
    slots,
    detailStatus,
}: {
    sale: BackofficeSale;
    slots?: SaleSlots;
    detailStatus?: SaleDetailStatus;
}) {
    const facts = ventaDetailFacts(sale);
    const alert = saleAlert(sale);
    return (
        <div className={TAB_BODY}>
            <SaleFactsSection sale={facts} />
            <PaymentSection sale={sale} />
            <AmountsSection sale={sale} />
            <CancellationSection sale={sale} />
            <CentralSection sale={sale} />
            <TabNote variant={ALERT_TONE_TO_NOTE[alert.tone] ?? 'info'}>{alert.text}</TabNote>
            <CancelSaleAction
                sale={sale}
                slots={slots}
                className={CANCEL_SALE_BUTTON}
                {...(detailStatus ? { detailStatus } : {})}
            />
        </div>
    );
}
